import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCustomFieldDefinitions,
  createCustomField,
  updateCustomField,
  deleteCustomField,
} from 'src/api';

export function customFieldsQueryKey(projectId: string) {
  return ['project', projectId, 'custom_fields'] as const;
}

function invalidateCustomFieldRelatedQueries(
  qc: ReturnType<typeof useQueryClient>,
  projectId: string
) {
  qc.invalidateQueries({ queryKey: customFieldsQueryKey(projectId) });
  // Refresh table visible-column lists so new custom fields appear by default
  qc.invalidateQueries({ queryKey: ['project', projectId], exact: false });
}

export function useCustomFieldDefinitions(projectId: string | null | undefined) {
  return useQuery({
    queryKey: customFieldsQueryKey(projectId || ''),
    queryFn: () => getCustomFieldDefinitions(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

export function useCreateCustomField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ category, field }: { category: string; field: any }) =>
      createCustomField(projectId, category, field),
    onSuccess: () => invalidateCustomFieldRelatedQueries(qc, projectId),
  });
}

export function useUpdateCustomField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      category,
      fieldId,
      field,
    }: {
      category: string;
      fieldId: string;
      field: any;
    }) => updateCustomField(projectId, category, fieldId, field),
    onSuccess: () => invalidateCustomFieldRelatedQueries(qc, projectId),
  });
}

export function useDeleteCustomField(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      category,
      fieldId,
    }: {
      category: string;
      fieldId: string;
    }) => deleteCustomField(projectId, category, fieldId),
    onSuccess: () => invalidateCustomFieldRelatedQueries(qc, projectId),
  });
}
